// Moving stock between batches (docs/plans/batch-split.md): stock with no batch, or one batch,
// split into others. Nothing new is stored: a move is a pair of adjustments, worked out by
// `planBatchSplit`, and the product's total never changes.
import { expiryFromShelfLife, isWholeDate, shelfLifeLabel, type ShelfLifeUnit } from '@ims/core'
import type { BatchLevel, ProductRow } from '@ims/data'
import { Badge, Button, Field, Input, Modal, Select, cx, useToast } from '@ims/ui'
import { Plus, Sparkles, Trash2, Layers } from '@ims/ui/icons'
import { useState } from 'react'
import { QtyInput, type Lookups } from '../components.tsx'
import { errorMessage } from '../hooks.ts'
import {
  followExpiry,
  noBatchLevels,
  planBatchSplit,
  shelfOf,
  splitProblem,
  type BatchRow,
  type ExpiryBox,
} from '../lib/batches.ts'
import { qty } from '../lib/format.ts'
import { batchOp } from '../lib/rows.ts'
import { useRepo, useWorkspace } from '../workspace.tsx'

type Shelf = { amount: number | null; unit: ShelfLifeUnit | null }

/**
 * The line under an expiry box: where the date came from, or, once someone typed their own,
 * a way back to the one worked out from the shelf life.
 */
export function ExpiryHint({
  box,
  mfg,
  shelf,
  onUse,
}: {
  box: ExpiryBox
  mfg: string
  shelf: Shelf
  onUse: (expiry: string) => void
}) {
  if (!shelf.amount) return null
  const label = shelfLifeLabel(shelf.amount, shelf.unit)
  if (box.auto && box.expiry) {
    return (
      <span>
        <Sparkles className="mr-1 inline size-3.5 align-[-2px] text-primary" />
        <b className="font-semibold text-primary">From shelf life</b>: mfg + {label}. Follows the
        mfg date.
      </span>
    )
  }
  const worked = isWholeDate(mfg) ? expiryFromShelfLife(mfg, shelf.amount, shelf.unit) : null
  if (box.expiry && worked && worked !== box.expiry) {
    return (
      <span>
        Typed by you, kept as it is.{' '}
        <button
          type="button"
          className="font-semibold text-primary hover:underline"
          onClick={() => onUse(worked)}
        >
          Use shelf life ({worked})
        </button>
      </span>
    )
  }
  return <span>Fills in from the mfg date (mfg + {label}).</span>
}

interface Line extends ExpiryBox {
  key: number
  batchNo: string
  mfg: string
  qtyMilli: number | null
}

const blank = (key: number): Line => ({
  key,
  batchNo: '',
  mfg: '',
  expiry: '',
  auto: false,
  qtyMilli: null,
})

/** Where stock can be split from: a batch with stock, or stock with no batch, at a place. */
export interface SplitSource {
  batchId: string | null
  locationId: string
}

/** The sources with stock: no batch first, then each batch, per place. */
function sourcesOf(
  levels: readonly { location_id: string; qty_milli: number }[],
  batchLevels: readonly BatchLevel[],
): { batchId: string | null; locationId: string; qtyMilli: number }[] {
  const out: { batchId: string | null; locationId: string; qtyMilli: number }[] = []
  for (const [locationId, qtyMilli] of noBatchLevels(levels, batchLevels)) {
    if (qtyMilli > 0) out.push({ batchId: null, locationId, qtyMilli })
  }
  for (const l of batchLevels) {
    if (l.qty_milli > 0)
      out.push({ batchId: l.batch_id, locationId: l.location_id, qtyMilli: l.qty_milli })
  }
  return out
}

export function canSplit(
  levels: readonly { location_id: string; qty_milli: number }[],
  batchLevels: readonly BatchLevel[],
): boolean {
  return sourcesOf(levels, batchLevels).length > 0
}

export function SplitBatchesDialog({
  product,
  batches,
  levels,
  batchLevels,
  lookups,
  unitSymbol,
  allowsDecimal,
  start,
  onClose,
}: {
  product: ProductRow
  batches: BatchRow[]
  levels: readonly { location_id: string; qty_milli: number }[]
  batchLevels: readonly BatchLevel[]
  lookups: Lookups
  unitSymbol: string
  allowsDecimal: boolean
  start?: SplitSource | null
  onClose: () => void
}) {
  const repo = useRepo()
  const { current } = useWorkspace()
  const toast = useToast()
  const sources = sourcesOf(levels, batchLevels)
  const first =
    sources.find((s) => s.batchId === start?.batchId && s.locationId === start?.locationId) ??
    sources[0]
  const [fromId, setFromId] = useState<string>(first?.batchId ?? '')
  const [locationId, setLocationId] = useState<string>(first?.locationId ?? '')
  const [lines, setLines] = useState<Line[]>([blank(1)])
  const [busy, setBusy] = useState(false)
  const shelf = shelfOf(product)
  const from = fromId ? (batches.find((b) => b.id === fromId) ?? null) : null
  const fromName = from?.batch_no ?? 'No batch'
  const places = sources.filter((s) => (s.batchId ?? '') === fromId)
  const here = places.find((s) => s.locationId === locationId) ?? places[0]
  const available = here?.qtyMilli ?? 0
  const placeName = (id: string) => lookups.location(id)?.name ?? 'Main store'

  const input = {
    productId: product.id,
    locationId: here?.locationId ?? '',
    from,
    availableMilli: available,
    lines: lines.map((l) => ({
      batchNo: l.batchNo,
      mfgDate: l.mfg,
      expiryDate: l.expiry,
      qtyMilli: l.qtyMilli,
    })),
    batches,
  }
  const problem = splitProblem(input)
  const placed = lines.reduce((a, l) => a + (l.qtyMilli ?? 0), 0)
  const left = available - placed
  const update = (key: number, change: Partial<Line>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...change } : l)))

  const onBatchNo = (l: Line, batchNo: string) => {
    // A number that already exists brings its dates; the move adds to that batch.
    const known = batches.find((b) => b.batch_no.toLowerCase() === batchNo.trim().toLowerCase())
    update(l.key, {
      batchNo,
      ...(known && !l.mfg && !l.expiry
        ? { mfg: known.mfg_date ?? '', expiry: known.expiry_date ?? '', auto: false }
        : {}),
    })
  }

  const save = async () => {
    if (!current || problem) return
    setBusy(true)
    try {
      const { ops, movedMilli } = planBatchSplit({ tenantId: current.tenant_id }, input)
      await repo.commit(ops)
      toast.success(`Moved ${qty(movedMilli)} ${unitSymbol} into batches`.replace(/\s+/g, ' '))
      onClose()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  const sourceLabel = (batchId: string | null) => {
    const total = sources.filter((s) => s.batchId === batchId).reduce((a, s) => a + s.qtyMilli, 0)
    const name = batchId ? (batches.find((b) => b.id === batchId)?.batch_no ?? '') : 'No batch'
    return `${name} · ${qty(total)} ${unitSymbol}`.trim()
  }
  const sourceIds = [...new Set(sources.map((s) => s.batchId ?? ''))]
  const full = placed > 0 && left === 0

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      area="stock"
      icon={<Layers className="size-5" />}
      title="Split into batches"
      footer={
        <>
          <p className="mr-auto text-sm text-muted">{placed > 0 && problem ? problem : null}</p>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={!!problem} onClick={() => void save()}>
            Split {placed > 0 ? `${qty(placed)} ${unitSymbol}`.trim() : ''}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="-mt-1 text-sm text-muted">{product.name}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Take from">
            {(id) => (
              <Select
                id={id}
                value={fromId}
                onChange={(e) => {
                  setFromId(e.target.value)
                  const at = sources.find((s) => (s.batchId ?? '') === e.target.value)
                  if (at) setLocationId(at.locationId)
                }}
              >
                {sourceIds.map((b) => (
                  <option key={b} value={b}>
                    {sourceLabel(b || null)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {places.length > 1 ? (
            <Field label="Where" hint="Only places that have it.">
              {(id) => (
                <Select
                  id={id}
                  value={here?.locationId ?? ''}
                  onChange={(e) => setLocationId(e.target.value)}
                >
                  {places.map((p) => (
                    <option key={p.locationId} value={p.locationId}>
                      {placeName(p.locationId)} · {qty(p.qtyMilli)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : (
            <div className="self-end pb-2 text-sm text-muted">
              {qty(available)} {unitSymbol} in {placeName(here?.locationId ?? '')}
            </div>
          )}
        </div>

        <ul className="grid gap-3" aria-label="Batches to move into">
          {lines.map((l, i) => {
            const known = batches.find(
              (b) =>
                l.batchNo.trim() && b.batch_no.toLowerCase() === l.batchNo.trim().toLowerCase(),
            )
            const n = i + 1
            return (
              <li
                key={l.key}
                className="relative grid grid-cols-2 gap-2 rounded-xl border border-border p-3 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1.15fr)_6rem_2.5rem] sm:items-start sm:border-0 sm:p-0"
              >
                <label className="block min-w-0">
                  <span className="mb-1 block text-xs font-medium text-muted">Batch no.</span>
                  <Input
                    aria-label={`Batch no. ${n}`}
                    value={l.batchNo}
                    onChange={(e) => onBatchNo(l, e.target.value)}
                  />
                  {l.batchNo.trim() && (
                    <Badge tone={known ? 'neutral' : 'success'} className="mt-1">
                      {known ? 'Adds to this batch' : 'New batch'}
                    </Badge>
                  )}
                </label>
                <label className="block min-w-0 max-sm:order-3">
                  <span className="mb-1 block text-xs font-medium text-muted">Mfg date</span>
                  <Input
                    aria-label={`Mfg date ${n}`}
                    type="date"
                    value={l.mfg}
                    onChange={(e) =>
                      update(l.key, {
                        mfg: e.target.value,
                        ...followExpiry(e.target.value, l, shelf),
                      })
                    }
                  />
                </label>
                <label className="block min-w-0 max-sm:order-4">
                  <span className="mb-1 flex items-center gap-1 text-xs font-medium text-muted">
                    Expiry date
                    {l.auto && l.expiry && (
                      <Sparkles className="size-3.5 text-primary" aria-label="From shelf life" />
                    )}
                  </span>
                  <Input
                    aria-label={`Expiry date ${n}`}
                    type="date"
                    value={l.expiry}
                    onChange={(e) => update(l.key, { expiry: e.target.value, auto: false })}
                  />
                </label>
                <label className="block min-w-0 max-sm:order-2">
                  <span className="mb-1 block text-xs font-medium text-muted">Quantity</span>
                  <QtyInput
                    aria-label={`Quantity ${n}`}
                    className="text-right tabular"
                    milli={l.qtyMilli}
                    allowsDecimal={allowsDecimal}
                    onValue={(m) => update(l.key, { qtyMilli: m })}
                  />
                </label>
                <div className="flex justify-end max-sm:absolute max-sm:right-1.5 max-sm:top-1.5 sm:pt-5">
                  <button
                    type="button"
                    aria-label={`Remove line ${n}`}
                    disabled={lines.length === 1}
                    onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                    className="tap-target grid size-9 place-items-center rounded-lg text-subtle hover:bg-soft hover:text-danger disabled:opacity-40"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
        <button
          type="button"
          onClick={() => setLines((ls) => [...ls, blank(Math.max(...ls.map((l) => l.key)) + 1)])}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
        >
          <Plus className="size-4" />
          Add a batch
        </button>

        <div className={cx('rounded-xl p-3', full ? 'bg-success-light' : 'bg-soft')}>
          <div
            className={cx(
              'flex justify-between gap-3 text-sm font-semibold',
              full ? 'text-success' : 'text-ink',
            )}
          >
            <span className="tabular">
              {qty(Math.min(placed, available))} of {qty(available)} placed
            </span>
            <span className="font-medium text-muted">
              {full
                ? 'All placed'
                : left > 0
                  ? `${qty(left)} stay in ${fromName}`
                  : 'More than there is'}
            </span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-border/70">
            <div
              className={cx(
                'h-full origin-left rounded-full transition-transform duration-200 ease-out',
                full ? 'bg-success' : left < 0 ? 'bg-danger' : 'bg-primary',
              )}
              style={{ transform: `scaleX(${available ? Math.min(placed / available, 1) : 0})` }}
            />
          </div>
        </div>
        <p className="text-xs text-muted">
          The total stays the same. Only which batch each one is in changes, and the history says
          so.
        </p>
      </div>
    </Modal>
  )
}

/**
 * A batch's mfg and expiry dates, set after it was received: for a batch saved without an
 * expiry, or with a wrong one. With a shelf life an empty expiry offers the worked-out date.
 */
export function EditBatchDialog({
  product,
  batch,
  onClose,
}: {
  product: ProductRow
  batch: BatchRow
  onClose: () => void
}) {
  const repo = useRepo()
  const { current } = useWorkspace()
  const toast = useToast()
  const shelf = shelfOf(product)
  const [mfg, setMfg] = useState(batch.mfg_date ?? '')
  const [box, setBox] = useState<ExpiryBox>({ expiry: batch.expiry_date ?? '', auto: false })
  const [busy, setBusy] = useState(false)
  const changed = mfg !== (batch.mfg_date ?? '') || box.expiry !== (batch.expiry_date ?? '')

  const save = async () => {
    if (!current || !changed) return onClose()
    setBusy(true)
    try {
      const { op } = batchOp(
        { tenantId: current.tenant_id },
        product.id,
        { batchNo: batch.batch_no, mfgDate: mfg || null, expiryDate: box.expiry || null },
        batch,
      )
      if (op) await repo.commit([op])
      toast.success(`Batch ${batch.batch_no} saved`)
      onClose()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={`Batch ${batch.batch_no}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={!changed} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <Field label="Mfg date">
          {(id) => (
            <Input
              id={id}
              type="date"
              value={mfg}
              onChange={(e) => {
                setMfg(e.target.value)
                setBox((b) => followExpiry(e.target.value, b, shelf))
              }}
            />
          )}
        </Field>
        <Field
          label="Expiry date"
          hint={
            product.track_expiry && shelf.amount ? (
              <ExpiryHint
                box={box}
                mfg={mfg}
                shelf={shelf}
                onUse={(expiry) => setBox({ expiry, auto: true })}
              />
            ) : undefined
          }
        >
          {(id) => (
            <Input
              id={id}
              type="date"
              value={box.expiry}
              onChange={(e) => setBox({ expiry: e.target.value, auto: false })}
            />
          )}
        </Field>
      </div>
      {!box.expiry && shelf.amount && isWholeDate(mfg) && (
        <Button
          variant="soft"
          size="sm"
          className="mt-3"
          onClick={() =>
            setBox({ expiry: expiryFromShelfLife(mfg, shelf.amount!, shelf.unit), auto: true })
          }
        >
          <Sparkles className="size-4" />
          Fill in from shelf life
        </Button>
      )}
    </Modal>
  )
}
